import React, { useState, useEffect } from "react";
import { Paper, Text, TextInput, Group, ActionIcon } from "@mantine/core";
import { IconCopy, IconCheck, IconX } from "@tabler/icons-react";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";

type SubmitStatus = "success" | "error" | null;

const WebsiteGoogleAnalytics: React.FC = () => {
  const { addGoogleAnalytics } = useTemplate();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [trackingId, setTrackingId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const [deploymentStatus, setDeploymentStatus] = useState(false);

  // Debounced auto-submit effect
  useEffect(() => {
    if (!trackingId.trim()) {
      setSubmitStatus(null);
      setDeploymentStatus(false);
      return;
    }

    // Check if salon ID is available
    if (!businessInfo?.id) {
      console.error(
        "Salon information not available for Google Analytics submission",
      );
      setSubmitStatus("error");
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsSubmitting(true);
        setSubmitStatus(null);

        const response = await addGoogleAnalytics(businessInfo.id, {
          google_analytics_tracking_id: trackingId.trim(),
        });

        setSubmitStatus("success");
        setDeploymentStatus(response.deployment_status || false);
        setIsSubmitting(false);
      } catch (error) {
        console.error("Google Analytics submission error:", error);
        setSubmitStatus("error");
        setDeploymentStatus(false);
        setIsSubmitting(false);
      }
    }, 2000); // 2 second delay

    return () => clearTimeout(timeoutId);
  }, [trackingId, addGoogleAnalytics, businessInfo?.id]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Prevent rapid changes that could cause multiple API calls
    setTrackingId(value);
  };

  // Handle blur validation
  const handleBlur = () => {
    // Enhanced validation for Google Analytics tracking ID
    if (trackingId.trim()) {
      const gaPattern = /^G-[A-Z0-9]+$/;
      const ga4Pattern = /^G-[A-Z0-9]{10}$/;

      if (!gaPattern.test(trackingId.trim())) {
        setSubmitStatus("error");
      }
    }
  };

  // Cleanup effect for component unmounting
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts when component unmounts
      setSubmitStatus(null);
      setIsSubmitting(false);
    };
  }, []);

  // Render status icon
  const renderStatusIcon = () => {
    if (isSubmitting) {
      return (
        <ActionIcon variant="light" color="blue" size="sm">
          <IconCheck size={16} />
        </ActionIcon>
      );
    }

    if (submitStatus === "success") {
      return (
        <ActionIcon variant="light" color="green" size="sm">
          <IconCheck size={16} />
        </ActionIcon>
      );
    }

    if (submitStatus === "error") {
      return (
        <ActionIcon variant="light" color="red" size="sm">
          <IconX size={16} />
        </ActionIcon>
      );
    }

    return null;
  };

  return (
    <Paper p="md" style={{ marginBottom: "20px" }}>
      <Text size="xl" fw={700} mb="md">
        Google Analytics
      </Text>

      <Text c="dimmed" mb="lg">
        Directly integrate Google Analytics into your website. Please note that
        as a site owner you are responsible for making your that your site is
        handling data in a way that is in line with privacy laws such as the
        GDPR.{" "}
        <Text component="a" href="#" c="blue" td="underline">
          Learn More.
        </Text>
      </Text>

      <Text fw={500} mb="xs">
        Google Analytics Measurement ID
      </Text>

      <Group>
        <TextInput
          placeholder="Google Analytics Key"
          value={trackingId}
          onChange={handleInputChange}
          onBlur={handleBlur}
          style={{ flex: 1 }}
          rightSection={renderStatusIcon()}
          rightSectionWidth={40}
        />
        <ActionIcon variant="light" color="gray">
          <IconCopy size={16} />
        </ActionIcon>
      </Group>

      {/* Deployment Status */}
      {deploymentStatus && (
        <Text size="sm" c="green" mt="xs" fw={500}>
          ✓ Deployed
        </Text>
      )}
    </Paper>
  );
};

export default WebsiteGoogleAnalytics;
